import {
  Box,
  Button,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Tower } from "../types/towerTypes";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import ConfirmBulkDeleteModal from "./ConfirmBulkDeleteModal";
import ColumnFilterMenu from "./ColumnFilterMenu";
import { TowerUsage } from "../api/towerApi";
import { ProjectActivity } from "../types/ActivityTypes";
import { ProjectActivityTowerResponse } from "../types/projectActivityTowerTypes";
import { assignTowerToActivity, getTowersByProjectActivity, removeTowerFromActivity } from "../api/projectActivityTowerApi";

/** Motivos por los que una torre no puede eliminarse. */
const BLOCKED_BY_REPORTS = 'No se puede eliminar: la torre ya tiene reportes diarios asociados.';
const BLOCKED_BY_ACTIVITIES = 'No se puede eliminar: la torre está asignada a una o más actividades.';

interface TowerTableProps {
  towers: Tower[];
  onEditTower: (updatedTower: Tower) => void;
  onDeleteTower: (towerId: number) => void;
  /** Eliminación múltiple. Si no se entrega, la selección múltiple no se muestra. */
  onDeleteMultipleTowers?: (towerIds: number[]) => Promise<void> | void;
  /** Torres que no se pueden eliminar, agrupadas por motivo. */
  towerUsage?: TowerUsage;
  onDownloadFormat: () => void;
  onImportTowers: (file: File) => void;
  onExportTowers: () => void;
  selectedActivities?: ProjectActivity[];
}

const TowerTable: React.FC<TowerTableProps> = ({ towers, onEditTower, onDeleteTower, onDeleteMultipleTowers, towerUsage, onDownloadFormat, onImportTowers, onExportTowers, selectedActivities = [] }) => {
  const [editingTowerNumber, setEditingTowerNumber] = useState<string | null>(null);
  const [editedTowerData, setEditedTowerData] = useState<Tower | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [towerId, setTowerId] = useState<number | null>(null);
  const [towerName, setTowerName] = useState('');
  const [activityTowerRelations, setActivityTowerRelations] = useState<ProjectActivityTowerResponse[]>([]);

  // Seleccion multiple para eliminar. Las torres bloqueadas SI se pueden seleccionar: el
  // modal avisa que se omitiran y elimina el resto.
  const [selectedTowerIds, setSelectedTowerIds] = useState<number[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const allowMultiSelect = !!onDeleteMultipleTowers;
  // const [activityFlags, setActivityFlags] = useState<Record<string, Record<number, boolean>>>({});
  // const [editedActivityFlags, setEditedActivityFlags] = useState<Record<number, boolean>>({});

  // --- BÚSQUEDA Y FILTROS POR COLUMNA ---
  /** Columnas del listado. El id es el campo de Tower, así el filtro es genérico. */
  const columnsDefinition: { id: keyof Tower; label: string }[] = [
    { id: 'towerNumber', label: 'Número de Torre' },
    { id: 'towerWeight', label: 'Peso Torre (kg)' },
    { id: 'forwardSpan', label: 'Vano Adelante (m)' },
    { id: 'vertexName', label: 'Nombre de Vértice' },
    { id: 'towerType', label: 'Tipo de Torre' },
    { id: 'soilType', label: 'Tipo de Suelo' },
    { id: 'lightRoadLength', label: 'Longitud Camino Liviano (m)' },
    { id: 'mediumRoadLength', label: 'Longitud Camino Medio (m)' },
    { id: 'heavyRoadLength', label: 'Longitud Camino Fuerte (m)' },
    { id: 'foundationPlan', label: 'Plano de Cimentación' },
    { id: 'excavationVolume', label: 'Excavación (m3)' },
    { id: 'concreteVolume', label: 'Hormigón (m3)' },
    { id: 'steelWeight', label: 'Acero (kg)' },
  ];

  const [filter, setFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(
    () => columnsDefinition.reduce((acc, col) => ({ ...acc, [col.id]: [] }), {} as Record<string, string[]>)
  );
  const [filterMenu, setFilterMenu] = useState<{ anchorEl: null | HTMLElement; columnKey: keyof Tower | null }>({
    anchorEl: null,
    columnKey: null,
  });

  /** Texto mostrado en la celda, que es también el valor por el que se filtra. */
  const getCellText = (tower: Tower, key: keyof Tower): string => {
    const value = tower[key];
    return value === null || value === undefined ? '' : String(value);
  };

  // Valores únicos de la columna cuyo menú está abierto. Las columnas numéricas se
  // ordenan por número para que no salga 1, 10, 2.
  const uniqueValues = useMemo<string[]>(() => {
    const key = filterMenu.columnKey;
    if (!key) return [];

    const values = Array.from(new Set(towers.map(tower => getCellText(tower, key)).filter(value => value !== '')));
    const allNumeric = values.every(value => !isNaN(Number(value)));
    return allNumeric ? values.sort((a, b) => Number(a) - Number(b)) : values.sort();
  }, [towers, filterMenu.columnKey]);

  const filteredTowers = useMemo(() => {
    const globalSearch = filter.trim().toLowerCase();

    return towers.filter(tower => {
      // --- ETAPA 1: BÚSQUEDA GLOBAL (lógica "O") ---
      if (globalSearch) {
        const found = columnsDefinition.some(col =>
          getCellText(tower, col.id).toLowerCase().includes(globalSearch)
        );
        if (!found) return false;
      }

      // --- ETAPA 2: FILTROS POR COLUMNA (lógica "Y") ---
      return columnsDefinition.every(col => {
        const selected = columnFilters[col.id];
        return !selected || selected.length === 0 || selected.includes(getCellText(tower, col.id));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [towers, filter, columnFilters]);

  const handleColumnFilterChange = (selected: string[]) => {
    if (!filterMenu.columnKey) return;
    setColumnFilters(prev => ({ ...prev, [filterMenu.columnKey as string]: selected }));
  };

  const clearFilters = () => {
    setFilter('');
    setColumnFilters(columnsDefinition.reduce((acc, col) => ({ ...acc, [col.id]: [] }), {} as Record<string, string[]>));
  };

  const hasActiveFilters = !!filter || Object.values(columnFilters).some(values => values.length > 0);

  useEffect(() => {
    const loadActivityTowerRelations = async () => {
      try {
        const activitiesWithId = selectedActivities.filter(
          pa => pa.id !== undefined && pa.id !== null
        );

        const responses = await Promise.all(
          activitiesWithId.map(pa => getTowersByProjectActivity(pa.id))
        );

        setActivityTowerRelations(responses.flat());
      } catch (error) {
        console.error("Error al cargar torres asociadas a actividades:", error);
      }
    };

    if (selectedActivities.length > 0) {
      loadActivityTowerRelations();
    } else {
      setActivityTowerRelations([]);
    }
  }, [selectedActivities]);

  const handleEditClick = (tower: Tower) => {
    setEditingTowerNumber(tower.towerNumber);
    setEditedTowerData({ ...tower });
  };

  const handleOpenConfirmModal = (id: number | undefined, name: any) => {
    setTowerId(id ?? null);
    setTowerName(name);
    setIsConfirmModalOpen(true);
  }

  const handleSaveClick = () => {
    if (editedTowerData) {
      onEditTower(editedTowerData);
    }
    setEditingTowerNumber(null);
    setEditedTowerData(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editedTowerData) {
      const newValue = e.target.value.replace(",", ".");
      setEditedTowerData({ ...editedTowerData, [e.target.name]: newValue });
    }
  };

  // NUEVO: Función específica para manejar los checkboxes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (editedTowerData) {
      setEditedTowerData({ ...editedTowerData, [e.target.name]: e.target.checked });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImportTowers(file);
    }
  };

  const handleOnDeleteTower = () => {
    if (towerId != null) {
      onDeleteTower(towerId);
    }
  }

  // ---------------------------------------------------------------------------
  // Bloqueo de eliminacion y seleccion multiple
  // ---------------------------------------------------------------------------

  const hasDailyReports = (id?: number) =>
    id != null && (towerUsage?.withDailyReports || []).includes(id);

  const hasActivityAssignments = (id?: number) =>
    id != null && (towerUsage?.withActivityAssignments || []).includes(id);

  const isDeleteBlocked = (id?: number) => hasDailyReports(id) || hasActivityAssignments(id);

  /** Tooltip del boton eliminar, con el motivo concreto del bloqueo. */
  const getDeleteTooltip = (id?: number): string => {
    if (hasDailyReports(id)) return BLOCKED_BY_REPORTS;
    if (hasActivityAssignments(id)) return BLOCKED_BY_ACTIVITIES;
    return 'Eliminar torre';
  };

  const towerLabel = (tower: Tower) => tower.towerNumber || `ID ${tower.id}`;

  const visibleTowerIds = filteredTowers.map(tower => tower.id).filter((id): id is number => id != null);
  const allVisibleSelected = visibleTowerIds.length > 0 && visibleTowerIds.every(id => selectedTowerIds.includes(id));
  const someVisibleSelected = selectedTowerIds.length > 0 && !allVisibleSelected;

  const toggleRowSelection = (id?: number) => {
    if (id == null) return;
    setSelectedTowerIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const toggleSelectAllVisible = () => {
    setSelectedTowerIds(allVisibleSelected ? [] : visibleTowerIds);
  };

  // La seleccion se limita a las filas visibles: si un filtro oculta una fila seleccionada,
  // se deselecciona para no eliminar torres que el usuario no esta viendo.
  useEffect(() => {
    setSelectedTowerIds(prev => {
      if (prev.length === 0) return prev;
      const visible = new Set(visibleTowerIds);
      const next = prev.filter(id => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTowers]);

  const selectedTowers = filteredTowers.filter(tower => tower.id != null && selectedTowerIds.includes(tower.id));
  const deletableTowers = selectedTowers.filter(tower => !isDeleteBlocked(tower.id));
  const blockedByReports = selectedTowers.filter(tower => hasDailyReports(tower.id));
  // Solo se cuenta como bloqueada por actividad si no lo estaba ya por reportes, para no
  // listar la misma torre dos veces en el modal.
  const blockedByActivities = selectedTowers.filter(
    tower => !hasDailyReports(tower.id) && hasActivityAssignments(tower.id)
  );

  const handleConfirmBulkDelete = async () => {
    if (!onDeleteMultipleTowers) return;
    setIsBulkDeleting(true);
    try {
      await onDeleteMultipleTowers(deletableTowers.map(tower => tower.id as number));
      setSelectedTowerIds([]);
      setIsBulkDeleteModalOpen(false);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const isTowerAssignedToActivity = (
    projectActivityId: number,
    towerId: number | undefined
  ): boolean => {
    return activityTowerRelations.some(
      relation =>
        relation.projectActivityId === projectActivityId &&
        relation.towerId === towerId &&
        relation.active
    );
  };

  const handleActivityTowerCheckboxChange = async (
    projectActivityId: number,
    towerId: number | undefined,
    checked: boolean
  ) => {
    if (towerId == null) return;
    try {
      if (checked) {
        const response = await assignTowerToActivity({
          projectActivityId,
          towerId
        });

        setActivityTowerRelations(prev => {
          const alreadyExists = prev.some(
            relation =>
              relation.projectActivityId === projectActivityId &&
              relation.towerId === towerId
          );

          if (alreadyExists) {
            return prev.map(relation =>
              relation.projectActivityId === projectActivityId &&
              relation.towerId === towerId
                ? { ...relation, active: true }
                : relation
            );
          }

          return [...prev, response];
        });
      } else {
        await removeTowerFromActivity(projectActivityId, towerId);

        setActivityTowerRelations(prev =>
          prev.map(relation =>
            relation.projectActivityId === projectActivityId &&
            relation.towerId === towerId
              ? { ...relation, active: false }
              : relation
          )
        );
      }
    } catch (error) {
      console.error("Error al actualizar relación actividad-torre:", error);
    }
  };

  return (
    <Grid2>
      <TextField
        label="Buscar torre por número, vértice, tipo o plano"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        fullWidth
        margin="normal"
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {filteredTowers.length === towers.length
            ? `${towers.length} torres`
            : `${filteredTowers.length} de ${towers.length} torres`}
        </Typography>
        {hasActiveFilters && (
          <Typography
            variant="body2"
            onClick={clearFilters}
            sx={{ color: '#004F86', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            Limpiar filtros
          </Typography>
        )}
      </Box>

      {/* Barra de acciones de selección múltiple */}
      {allowMultiSelect && selectedTowerIds.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, p: 1.5, borderRadius: 1, backgroundColor: '#e3f2fd' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#004F86' }}>
            {selectedTowerIds.length === 1 ? '1 torre seleccionada' : `${selectedTowerIds.length} torres seleccionadas`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setSelectedTowerIds([])}>
              Quitar selección
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setIsBulkDeleteModalOpen(true)}
            >
              Eliminar seleccionadas
            </Button>
          </Box>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 700, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {allowMultiSelect && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    title="Seleccionar todo lo visible"
                  />
                </TableCell>
              )}
              {columnsDefinition.map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: 'bold', color: '#004F86', verticalAlign: 'top', p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {col.label}
                    <IconButton
                      size="small"
                      title={`Filtrar por ${col.label}`}
                      onClick={(e) => setFilterMenu({ anchorEl: e.currentTarget, columnKey: col.id })}
                      sx={{ color: columnFilters[col.id]?.length > 0 ? '#004F86' : 'inherit' }}
                    >
                      <FilterListIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              ))}
              {/* <TableCell sx={{ fontWeight: 'bold', color: '#004F86', textAlign: 'center' }}>Pintura de señales</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#004F86', textAlign: 'center' }}>Instalación de balizas</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#004F86', textAlign: 'center' }}>Instalación Avifauna</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#004F86', textAlign: 'center' }}>Empalmes OPGW</TableCell> */}
              {selectedActivities.map(pa => (
                <TableCell key={pa.id} sx={{ fontWeight: 'bold', color: '#004F86', textAlign: 'center' }}>
                  {pa.activity?.name}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTowers.map((tower) => (
              <TableRow key={tower.id ?? tower.towerNumber} selected={tower.id != null && selectedTowerIds.includes(tower.id)}>
                {allowMultiSelect && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={tower.id != null && selectedTowerIds.includes(tower.id)}
                      onChange={() => toggleRowSelection(tower.id)}
                      disabled={editingTowerNumber === tower.towerNumber}
                    />
                  </TableCell>
                )}
                <TableCell>{tower.towerNumber}</TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="towerWeight" value={editedTowerData?.towerWeight || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.towerWeight)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="forwardSpan" value={editedTowerData?.forwardSpan || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.forwardSpan)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="vertexName" value={editedTowerData?.vertexName || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.vertexName)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="towerType" value={editedTowerData?.towerType || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.towerType)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="soilType" value={editedTowerData?.soilType || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.soilType)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="lightRoadLength" value={editedTowerData?.lightRoadLength || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.lightRoadLength)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="mediumRoadLength" value={editedTowerData?.mediumRoadLength || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.mediumRoadLength)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="heavyRoadLength" value={editedTowerData?.heavyRoadLength || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.heavyRoadLength)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="foundationPlan" value={editedTowerData?.foundationPlan || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.foundationPlan)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="excavationVolume" value={editedTowerData?.excavationVolume || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.excavationVolume)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="concreteVolume" value={editedTowerData?.concreteVolume || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.concreteVolume)}
                </TableCell>
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <TextField name="steelWeight" value={editedTowerData?.steelWeight || ''} onChange={handleInputChange} fullWidth />
                  ) : (tower.steelWeight)}
                </TableCell>

                {/* <TableCell align="center">
                  <Checkbox
                    name="pinturaSenales"
                    checked={editingTowerNumber === tower.towerNumber ? (editedTowerData?.pinturaSenales || false) : (tower.pinturaSenales || false)}
                    onChange={handleCheckboxChange}
                    disabled={editingTowerNumber !== tower.towerNumber}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    name="instalacionBalizas"
                    checked={editingTowerNumber === tower.towerNumber ? (editedTowerData?.instalacionBalizas || false) : (tower.instalacionBalizas || false)}
                    onChange={handleCheckboxChange}
                    disabled={editingTowerNumber !== tower.towerNumber}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    name="instalacionAvifauna"
                    checked={editingTowerNumber === tower.towerNumber ? (editedTowerData?.instalacionAvifauna || false) : (tower.instalacionAvifauna || false)}
                    onChange={handleCheckboxChange}
                    disabled={editingTowerNumber !== tower.towerNumber}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Checkbox
                    name="empalmesOpgw"
                    checked={editingTowerNumber === tower.towerNumber ? (editedTowerData?.empalmesOpgw || false) : (tower.empalmesOpgw || false)}
                    onChange={handleCheckboxChange}
                    disabled={editingTowerNumber !== tower.towerNumber}
                    size="small"
                  />
                </TableCell> */}

                {selectedActivities.map(pa => {
                  const projectActivityId = Number(pa.id);

                  return (
                    <TableCell key={pa.id} align="center">
                      <Checkbox
                        size="small"
                        checked={isTowerAssignedToActivity(projectActivityId, tower.id)}
                        onChange={(e) =>
                          handleActivityTowerCheckboxChange(
                            projectActivityId,
                            tower.id,
                            e.target.checked
                          )
                        }
                        disabled={editingTowerNumber !== tower.towerNumber}
                      />
                    </TableCell>
                  );
                })}
                <TableCell>
                  {editingTowerNumber === tower.towerNumber ? (
                    <>
                      <IconButton onClick={handleSaveClick}>
                        <SaveIcon color="primary" />
                      </IconButton>
                      <IconButton color="error" onClick={() => { setEditingTowerNumber(null); setEditedTowerData(null); }}>
                        Cancelar
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => handleEditClick(tower)}>
                        <EditIcon color="primary" />
                      </IconButton>
                      <Tooltip title={getDeleteTooltip(tower.id)}>
                        <span>
                          <IconButton
                            onClick={() => handleOpenConfirmModal(tower.id, tower.towerNumber || '')}
                            disabled={isDeleteBlocked(tower.id)}
                            sx={{ color: isDeleteBlocked(tower.id) ? 'gray' : 'primary.main' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ColumnFilterMenu
        anchorEl={filterMenu.anchorEl}
        values={uniqueValues}
        selected={filterMenu.columnKey ? (columnFilters[filterMenu.columnKey] || []) : []}
        onClose={() => setFilterMenu({ anchorEl: null, columnKey: null })}
        onChange={handleColumnFilterChange}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="contained" color="primary" onClick={onDownloadFormat}>
          Descargar Formato
        </Button>

        <input
          accept=".xlsx, .xls"
          style={{ display: 'none' }}
          id="import-file"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="import-file">
          <Button variant="contained" color="primary" component="span">
            Importar Torres
          </Button>
        </label>

        <Button variant="contained" color="primary" onClick={onExportTowers}>
          Exportar Torres
        </Button>
      </Box>
      <ConfirmDeleteModal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={handleOnDeleteTower} id={towerId} name={towerName} title={'TOWER'}></ConfirmDeleteModal>

      <ConfirmBulkDeleteModal
        open={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        items={deletableTowers.map(towerLabel)}
        entityLabel="torre"
        entityLabelPlural="torres"
        blockedGroups={[
          { reason: 'Tienen reportes diarios asociados:', items: blockedByReports.map(towerLabel) },
          { reason: 'Están asignadas a actividades:', items: blockedByActivities.map(towerLabel) }
        ]}
        loading={isBulkDeleting}
      />
    </Grid2>
  );
};

export default TowerTable;